import { query } from 'bitecs';
import { Lifetime, Position, Projectile, Radius, Sprite, SpriteId, Velocity } from '@src/ecs/components';
import type { GameSystem } from '@src/ecs/System';
import { emitParticles, ParticleKind, type GameContext } from '@src/ecs/world';
import { FirePattern, weaponDef } from '@src/game/weapons/WeaponDefs';

/** Ticks between trail puffs per projectile (spread across entity ids). */
const TRAIL_EVERY = 5;

/** Visual bookkeeping for in-flight shots: facing, shockwave growth, arc fade, trails. */
export class ProjectileSystem implements GameSystem {
  private ctx!: GameContext;
  private tick = 0;

  init(ctx: GameContext): void {
    this.ctx = ctx;
  }

  destroy(): void {}

  update(dt: number): void {
    const ctx = this.ctx;
    this.tick++;
    const projectiles = query(ctx.world, [Projectile, Sprite, Radius, Lifetime]);
    for (let i = 0; i < projectiles.length; i++) {
      const eid = projectiles[i];
      const grow = Projectile.grow[eid];
      if (grow > 0) {
        Radius.r[eid] += grow * dt;
        Sprite.scale[eid] = Radius.r[eid] * 2;
        Sprite.alpha[eid] = Math.max(0, Math.min(1, Lifetime.t[eid] * 2.5));
        continue;
      }
      const vx = Velocity.x[eid];
      const vy = Velocity.y[eid];
      if (vx !== 0 || vy !== 0) Sprite.rot[eid] = Math.atan2(vy, vx);
      const id = Sprite.id[eid];
      if (id === SpriteId.BoltFire) {
        const def = weaponDef(Projectile.weapon[eid]);
        if (def.pattern === FirePattern.Crescent) {
          // Cleaver hitboxes are invisible; the slash FX sprite carries the visual.
          Sprite.alpha[eid] = 0;
          continue;
        }
        Sprite.alpha[eid] = Math.min(1, Lifetime.t[eid] * 3);
      }
      if ((eid + this.tick) % TRAIL_EVERY === 0) {
        const tint = id === SpriteId.BoltFire ? 0xff9a3a : id === SpriteId.BoltPhase ? 0xd08cff : 0x9af6ff;
        emitParticles(ctx.fx, ParticleKind.BoltTrail, Position.x[eid], Position.y[eid], vx, vy, id === SpriteId.BoltFire ? 1.4 : 0.8, tint);
      }
    }
  }
}
